import { SWRConfig } from 'swr';
import styled from '@emotion/styled';
import SEO from '@seo/index';
import { Spacings } from '@styles/index';
import Image from '@base/Image';
import Link from '@base/Link/Link';
import { emptyList } from '@constants/empty';
import FreeTrial from '@cont/FreeTrial/FreeTrial';
import { fetchOnboardingGoals } from '@api/fetchers/goal';
import { fetchRootPageData } from '@api/fetchers/home';
import Layout from '@layout/FreeLayout/FreeLayout';
import useHomeInfo from '@api/hooks/home/useHomeInfo';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { FREE_TRIAL_DAYS } from '@constants/index';

const Header = styled.div`
  display: flex;
  padding: ${Spacings.SPACING_4B};
  position: absolute;
  width: 100%;
  top: 0;
  background-color: var(--color-base-1);
  z-index: 1;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 1136px;
  margin: auto;
`;

const FreeTrialPage = ({ fallbackData, onboardingGoals }) => {
  const url = 'https://unacademy.com';
  const { keys } = useHomeInfo(fallbackData);

  return (
    <>
      <SWRConfig
        value={{
          dedupingInterval: 60 * 1000,
          fallback: {
            [keys.rootPageDataKey]: fallbackData?.rootPageData
          }
        }}
      >
        <SEO
          url={url}
          openGraph={{ url }}
          title={`Unacademy ${FREE_TRIAL_DAYS} day free trial`}
          description={`Unlock unlimited access to Unacademy's plus subscription for ${FREE_TRIAL_DAYS} days for free. Choose goal of your choice and get started.`}
        />
        <Header>
          <Logo>
            <Link href="/" as="/">
              <Image src="logo.svg" width={134} height={20} alt="Unacademy" />
            </Link>
          </Logo>
        </Header>
        <FreeTrial onboardingGoals={onboardingGoals} />
      </SWRConfig>
    </>
  );
};

FreeTrialPage.pageInfo = {
  name: 'Free Trial',
  isMWebSupported: true,
  layoutOptions: {
    hideSearch: true,
    hideHeader: true,
    hideGoalSelector: true,
    hideLearnerMinutes: true,
    hideNudges: true,
    disableDarkMode: true,
    hideIntercom: true,
    hideFreshbot: true
  }
};

export const getStaticProps = async () => {
  const promises = [fetchRootPageData(), fetchOnboardingGoals(false, false)];
  try {
    const [rootPageData, onboardingGoals] = await Promise.all(promises);
    return {
      props: {
        onboardingGoals: onboardingGoals?.results || emptyList,
        fallbackData: {
          rootPageData
        }
      },
      revalidate: 10 * 60
    };
  } catch (e) {
    return { notFound: true, revalidate: 10 };
  }
};

export default FreeTrialPage;
FreeTrialPage.Layout = Layout;
FreeTrialPage.Wrapper = PageReduxProvider;
